import { request, USE_MOCKS } from './client'

// ---------------------------------------------------------------------------
// Normalisers — map raw backend records to a stable UI shape with canonical
// status states, so components never branch on backend strings. Fields the
// backend model doesn't have yet (device location/signal/units) surface as null.
// ---------------------------------------------------------------------------
const PAYMENT_STATUS = {
  COMPLETED: { state: 'success', label: 'Completed' },
  PENDING: { state: 'pending', label: 'Pending' },
  FAILED: { state: 'failed', label: 'Failed' },
}

function normPayment(p) {
  const s = PAYMENT_STATUS[p.status] || { state: 'idle', label: p.status || '—' }
  return {
    id: p._id,
    phone: p.phone || '—',
    amount: p.amount || 0,
    method: p.method || '—',
    statusState: s.state,
    statusLabel: s.label,
    mpesaCode: p.mpesaCode || '—',
    units: p.unitsDispensed || 0,
    date: p.createdAt || null,
  }
}

function normDevice(d) {
  const online = d.status === 'active'
  return {
    id: d._id,
    name: d.name || '—',
    serial: d.serial || null,
    statusState: online ? 'success' : 'idle',
    statusLabel: online ? 'Online' : 'Offline',
    lastActive: d.lastActive || null,
    location: d.location || null,
    signal: d.signal || null,
    refillLevel: typeof d.refillLevel === 'number' ? d.refillLevel : null,
    unitsDispensed: typeof d.unitsDispensed === 'number' ? d.unitsDispensed : null,
  }
}

// A device needs a refill when reported level is at/below this %.
export const LOW_REFILL_THRESHOLD = 20

function normUser(u) {
  return {
    id: u._id,
    username: u.username,
    role: u.role || 'user',
    createdAt: u.createdAt || null,
  }
}

// ---------------------------------------------------------------------------
// Mocks (used only when REACT_APP_USE_MOCKS=true)
// ---------------------------------------------------------------------------
const MOCKS = {
  payments: [
    { _id: 'm1', phone: '254712345678', amount: 500, method: 'STK', status: 'COMPLETED', mpesaCode: 'QJD7H2K9L1', unitsDispensed: 5000, createdAt: '2026-05-19T08:45:00Z' },
    { _id: 'm2', phone: '254798112233', amount: 1200, method: 'C2B', status: 'COMPLETED', mpesaCode: 'QJD7H5T4R8', unitsDispensed: 12000, createdAt: '2026-05-19T09:10:00Z' },
    { _id: 'm3', phone: '254701223344', amount: 300, method: 'STK', status: 'PENDING', mpesaCode: '', unitsDispensed: 0, createdAt: '2026-05-19T09:42:00Z' },
  ],
  devices: [
    { _id: 'd1', name: 'DSP-001', status: 'active', lastActive: '2026-05-19T10:15:00Z' },
    { _id: 'd2', name: 'DSP-002', status: 'inactive', lastActive: '2026-05-19T08:40:00Z' },
  ],
  users: [{ _id: 'u1', username: 'admin', role: 'admin', createdAt: '2026-05-01T00:00:00Z' }],
}

const mock = (data) => new Promise((r) => setTimeout(() => r(data), 250))

// ---------------------------------------------------------------------------
// Services
// ---------------------------------------------------------------------------
export const authApi = {
  // Login never carries a token and is always live (mocks can't authenticate).
  login: (username, password) =>
    request('/users/login', { method: 'POST', auth: false, body: { username, password } }),
}

export const paymentsApi = {
  async list() {
    const raw = USE_MOCKS ? await mock(MOCKS.payments) : await request('/payments/get-payments')
    return (raw || []).map(normPayment)
  },
  create: (payload) => request('/payments/create-payment', { method: 'POST', body: payload }),
}

export const devicesApi = {
  async list() {
    const raw = USE_MOCKS ? await mock(MOCKS.devices) : await request('/device/get-devices')
    return (raw || []).map(normDevice)
  },
  create: (payload) => request('/device/create-device', { method: 'POST', body: payload }),
}

export const usersApi = {
  async list() {
    const raw = USE_MOCKS ? await mock(MOCKS.users) : await request('/users/get-users')
    return (raw || []).map(normUser)
  },
  create: (payload) => request('/users/create-user', { method: 'POST', body: payload }),
}
